import { safeStorage } from "../../../../../shared/lib/safeStorage";

/**
 * THE TIMES A PREVIOUS CLIENT CHOSE IN THIS TAB, so Back never re-selects them
 * for the next person (QE-SO-2), across reloads and remounts too (rc.9 F3).
 * Design: docs/planning/rc9-flow-record.md (security audit F3D-1..8; the Web
 * Director's ruling F3D-3 (a)).
 *
 * ONE sessionStorage key, written with one setItem, mirrored in memory and
 * loaded once per document. Render code never touches storage: it reads the
 * mirror (`useFlowRecordVersion` re-renders on change). A refused write keeps
 * the mirror, which then holds for the rest of the document.
 *
 * - `current`: valid times the CURRENT identity chose (or arrived on) here.
 * - `dropped`: times of signed-in clients who gave way.
 * - `tag`: a 16-hex FNV-1a of salt and uid for the identity `current` belongs to, with a
 *   random per-tab salt. A reload into a DIFFERENT account sees a mismatch.
 *   It cannot be turned back into the uid, and it never leaves this tab.
 * No uid, name, email, professional, service, booking or token is stored.
 */

export const FLOW_RECORD_KEY = "lura.book.flow-record";

const VERSION = 1;
const MAX_RAW = 8192;
const CAP = 96;
const PRUNE_AFTER_MS = 24 * 60 * 60 * 1000;
const TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/;
const SALT = /^[0-9a-f]{32,64}$/;
const TAG = /^[0-9a-f]{16}$/;

/** A resolved identity: a uid, signed in or not. `null` is "nobody". */
export type FlowIdentity = { readonly uid: string; readonly credentialed: boolean } | null;

interface StoredRecord {
  salt: string;
  tag: string | null;
  credentialed: boolean;
  current: string[];
  dropped: string[];
}

/** A slot start Book may record: strict ISO-UTC, short, a real instant. */
export function isRecordableTime(value: unknown): value is string {
  return typeof value === "string" && value.length <= 40 && TIME.test(value) && !Number.isNaN(Date.parse(value));
}

/** FNV-1a, 32 bit, as 8 hex digits. Not a secret: a collision only means "keep". */
export function fnv1a32(text: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}

/**
 * 16 hex digits (two FNV-1a rounds): long enough that error reporting's
 * scrubber removes it from any text it might ever reach (its hex rule takes
 * runs of 16 or more), though nothing ever sends it.
 */
export function tagOf(salt: string, uid: string): string {
  return fnv1a32(salt + uid) + fnv1a32(uid + salt);
}

function freshSalt(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function emptyRecord(): StoredRecord {
  return { salt: freshSalt(), tag: null, credentialed: false, current: [], dropped: [] };
}

function times(value: unknown, now: number): string[] | null {
  if (!Array.isArray(value)) return null;
  const kept = value.filter((item): item is string => isRecordableTime(item) && Date.parse(item) + PRUNE_AFTER_MS > now);
  return [...new Set(kept)].slice(-CAP);
}

/** The stored record, or null when it is missing or not exactly v1's shape. Never throws. */
function parse(raw: string | null, now: number): StoredRecord | null {
  if (raw === null || raw.length > MAX_RAW) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const own = (key: string): unknown => (Object.prototype.hasOwnProperty.call(value, key) ? (value as { [key: string]: unknown })[key] : undefined);
  const salt = own("salt");
  const tag = own("tag");
  const credentialed = own("credentialed");
  const current = times(own("current"), now);
  const dropped = times(own("dropped"), now);
  if (own("v") !== VERSION) return null;
  if (typeof salt !== "string" || !SALT.test(salt)) return null;
  if (tag !== null && (typeof tag !== "string" || !TAG.test(tag))) return null;
  if (typeof credentialed !== "boolean" || current === null || dropped === null) return null;
  return { salt, tag, credentialed, current, dropped };
}

let mirror: StoredRecord | null = null;
/** What this document last read from or wrote to storage. */
let storedText: string | null = null;
/** True when this document found no usable record: a fresh salt, nothing carried over. */
let loadedFresh = false;
/** The last resolved identity noted in this document; undefined until the first. */
let lastNote: FlowIdentity | undefined;
let version = 0;
const listeners = new Set<() => void>();

function record(): StoredRecord {
  if (mirror) return mirror;
  storedText = safeStorage.session.get(FLOW_RECORD_KEY);
  const parsed = parse(storedText, Date.now());
  loadedFresh = parsed === null;
  mirror = parsed ?? emptyRecord();
  return mirror;
}

function serialise(record: StoredRecord): string {
  return JSON.stringify({ v: VERSION, salt: record.salt, tag: record.tag, credentialed: record.credentialed, current: record.current, dropped: record.dropped });
}

/**
 * Store a record, and announce it only when it CHANGED BY VALUE from the one
 * this document holds (IF-3 commit guard, rc.9): a subscriber notified of a
 * no-op re-renders Book for nothing, an extra urgent commit
 * (tests/e2e/book/slots-tap-commits.spec.js). The write still happens when
 * storage holds something else: the record held may be the load's pruned
 * copy of what is stored.
 */
function commit(next: StoredRecord): void {
  const text = serialise(next);
  const changed = mirror === null || serialise(mirror) !== text;
  mirror = next;
  if (text !== storedText && safeStorage.session.set(FLOW_RECORD_KEY, text)) storedText = text;
  if (!changed) return;
  version += 1;
  for (const listener of listeners) listener();
}

function withTimes(list: readonly string[], add: readonly string[]): string[] {
  const merged = list.filter((time) => !add.includes(time));
  for (const time of add) merged.push(time);
  return [...new Set(merged)].slice(-CAP);
}

function sameIdentity(a: FlowIdentity | undefined, b: FlowIdentity): boolean {
  if (a === undefined) return false;
  if (a === null || b === null) return a === b;
  return a.uid === b.uid && a.credentialed === b.credentialed;
}

type Plan = { giveWay: boolean; clear: boolean };

/**
 * What noting `next` would do, without doing it (pure: render may ask).
 * The first resolved note of a document follows the LOAD table; later ones
 * the IN-PAGE table (design doc). Only a give-way drops, and only a guest
 * changing to another guest in the page clears.
 */
function planFor(next: FlowIdentity, stored: StoredRecord): Plan {
  if (lastNote === undefined) {
    // No usable record: a fresh salt, and a give-way of nothing. Never a
    // carry-over (Web Director).
    if (loadedFresh) return { giveWay: true, clear: false };
    if (!stored.credentialed) return { giveWay: false, clear: false };
    if (next === null || !next.credentialed) return { giveWay: true, clear: false };
    return { giveWay: stored.tag !== tagOf(stored.salt, next.uid), clear: false };
  }
  const previous = lastNote;
  if (previous !== null && previous.credentialed) {
    return { giveWay: next === null || next.uid !== previous.uid, clear: false };
  }
  if (previous !== null && next !== null && !next.credentialed && next.uid !== previous.uid) {
    return { giveWay: false, clear: true };
  }
  return { giveWay: false, clear: false };
}

export type NoteOutcome = "unchanged" | "kept" | "gave-way";

/**
 * Note a RESOLVED identity (never while it is loading). Returns "gave-way"
 * when a signed-in client's times were just dropped, IN THE PAGE (the caller
 * clears the details and outcome it holds); a give-way at load only moves
 * stored times, and is "kept" for the page, which holds nothing yet.
 */
export function noteIdentity(next: FlowIdentity, chosenTime: string | null): NoteOutcome {
  if (sameIdentity(lastNote, next)) return "unchanged";
  const stored = record();
  const atLoad = lastNote === undefined;
  const { giveWay, clear } = planFor(next, stored);
  const tag = next === null ? null : tagOf(stored.salt, next.uid);
  const credentialed = next !== null && next.credentialed;
  lastNote = next;
  if (giveWay) {
    const leaving = !atLoad && isRecordableTime(chosenTime) ? [...stored.current, chosenTime] : stored.current;
    commit({ ...stored, tag, credentialed, current: [], dropped: withTimes(stored.dropped, leaving) });
    return atLoad ? "kept" : "gave-way";
  }
  commit({ ...stored, tag, credentialed, current: clear ? [] : stored.current });
  return "kept";
}

/**
 * Whether the URL's time must not be shown: a Back/Forward (or a first load)
 * onto a time a previous client chose, that the current identity did not.
 * Pure, for render. `identity` undefined means still loading (nothing shown).
 */
export function isDroppedTime(time: string | null, identity: FlowIdentity | undefined, popped: boolean): boolean {
  if (!popped || identity === undefined || !isRecordableTime(time)) return false;
  const stored = record();
  let dropped = stored.dropped;
  let current = stored.current;
  if (!sameIdentity(lastNote, identity) && planFor(identity, stored).giveWay) {
    dropped = withTimes(dropped, current);
    current = [];
  }
  return dropped.includes(time) && !current.includes(time);
}

/** The current identity chose (or arrived on) `time`: it is theirs, never a dropped one. */
export function recordTime(time: string | null): void {
  if (lastNote === undefined || !isRecordableTime(time)) return;
  const stored = record();
  if (stored.current[stored.current.length - 1] === time && !stored.dropped.includes(time)) return;
  commit({ ...stored, current: withTimes(stored.current, [time]), dropped: stored.dropped.filter((item) => item !== time) });
}

export function subscribeFlowRecord(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function flowRecordVersion(): number {
  return version;
}

/**
 * Tests only: forget this document's mirror and notes, as a new document
 * would. `keepStorage` models a reload (the tab's sessionStorage survives it).
 */
export function __resetFlowRecordForTests({ keepStorage = false }: { keepStorage?: boolean } = {}): void {
  if (!keepStorage) safeStorage.session.remove(FLOW_RECORD_KEY);
  mirror = null;
  storedText = null;
  loadedFresh = false;
  lastNote = undefined;
  version += 1;
}
